import React from 'react';
import {
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Box,
  Text,
  Icon,
  useColorModeValue,
  HStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Settlement } from 'src/utils/__generated__/graphql';
import { FiCalendar, FiDollarSign, FiShoppingBag, FiUser } from 'react-icons/fi';

interface OrderTableProps {
  orders: Settlement[];
  orderType: 'settled' | 'unsettled';
}

export const OrderTable: React.FC<OrderTableProps> = ({ orders, orderType }) => {
  const navigate = useNavigate();

  // Color mode values
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const linkColor = useColorModeValue('brand.600', 'brand.300');
  const accentColor = useColorModeValue('brand.500', 'brand.300');
  const rowBorderColor = useColorModeValue('gray.100', 'gray.700');

  const handleRowClick = (order: Settlement) => {
    navigate(`/transcation/${orderType === 'unsettled' ? order.restaurant?.id : order.id}?type=${orderType}`);
  };

  return (
    <Box overflowX="auto" width="100%">
      <Table variant="simple" w="full" size="lg">
        <Thead bg={headerBg}>
          <Tr>
            <Th borderColor={borderColor} py={5} fontSize="md" color={textColor}>
              <HStack spacing={2}>
                <Icon as={FiUser as any} color={accentColor} boxSize={5} />
                <Text>Restaurant</Text>
              </HStack>
            </Th>
            <Th borderColor={borderColor} py={5} fontSize="md" color={textColor}>
              <HStack spacing={2}>
                <Icon as={FiCalendar as any} color={accentColor} boxSize={5} />
                <Text>Date</Text>
              </HStack>
            </Th>
            <Th borderColor={borderColor} py={5} isNumeric fontSize="md" color={textColor}>
              <HStack spacing={2} justifyContent="flex-end">
                <Icon as={FiShoppingBag as any} color={accentColor} boxSize={5} />
                <Text>Orders</Text>
              </HStack>
            </Th>
            <Th borderColor={borderColor} py={5} isNumeric fontSize="md" color={textColor}>
              <HStack spacing={2} justifyContent="flex-end">
                <Icon as={FiDollarSign as any} color={accentColor} boxSize={5} />
                <Text>Total</Text>
              </HStack>
            </Th>
            {/* <Th borderColor={borderColor} py={5} isNumeric fontSize="md" color={textColor}>
              <HStack spacing={2} justifyContent="flex-end">
                <Icon as={FiPackage as any} color={accentColor} boxSize={5} />
                <Text>Dish Amount</Text>
              </HStack>
            </Th> */}
            <Th borderColor={borderColor} py={5} isNumeric fontSize="md" color={textColor}>
              <HStack spacing={2} justifyContent="flex-end">
                <Icon as={FiDollarSign as any} color="purple.500" boxSize={5} />
                <Text>Admin Commission</Text>
              </HStack>
            </Th>
            <Th borderColor={borderColor} py={5} isNumeric fontSize="md" color={textColor}>
              <HStack spacing={2} justifyContent="flex-end">
                <Icon as={FiDollarSign as any} color="blue.500" boxSize={5} />
                <Text>Restaurant Earning</Text>
              </HStack>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {orders?.map((order, index) => {
            const total = (parseFloat(order.totalRestaurantEarning) + parseFloat(order.totalAdminEarning)).toFixed(2);
            const formattedDate = new Date(order.created_at).toLocaleDateString('en-US', {
              // year: 'numeric',
              month: 'short',
              day: 'numeric',
            });

            const orderCount = order.orders_aggregate.aggregate?.count || 0;

            return (
              <Tr
                key={`${order.id}${index}`}
                _hover={{
                  bg: hoverBg,
                  cursor: 'pointer',
                  transform: 'translateY(-2px)',
                  boxShadow: 'sm',
                }}
                onClick={() => handleRowClick(order)}
                transition="all 0.2s"
                borderBottom="1px"
                borderColor={rowBorderColor}
              >
                <Td py={4} fontSize="md" fontWeight="medium" color={linkColor}>
                  {order.restaurant?.name}
                </Td>
                <Td py={4} fontSize="md">
                  {formattedDate}
                </Td>
                <Td isNumeric py={4} fontSize="md">
                  {orderCount}
                </Td>
                <Td isNumeric py={4} fontSize="md" fontWeight="semibold" color="green.600">
                  Rs. {total}
                </Td>
                {/* <Td isNumeric py={4} fontSize="md">
                  Rs. {parseFloat(order.dishAmount).toFixed(2)}
                </Td> */}
                <Td isNumeric py={4} fontSize="md" color="purple.600">
                  Rs. {parseFloat(order.totalAdminEarning).toFixed(2)}
                </Td>
                <Td isNumeric py={4} fontSize="md" color="blue.600">
                  Rs. {parseFloat(order.totalRestaurantEarning).toFixed(2)}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};
